/* global axios */
import ApiClient from './ApiClient';
import store from '../store';
import webhookService from '../services/kanban/webhookService';
// SiteUp: popup de agendamento WAHA — emitimos um evento global sempre que um
// item é salvo com um agendamento (scheduling_type='schedule'), independente de
// QUAL tela disparou (painel da conversa, card em tela cheia, Agenda). É o
// equivalente limpo da antiga interceptação de XHR do script injetado.
import { emitter } from 'shared/helpers/mitt';

class KanbanAPI extends ApiClient {
  constructor() {
    super('kanban_items', { accountScoped: true });
  }


  // SiteUp: impede /kanban_items/null|/undefined|NaN (JS null stringificado na URL).
  // eslint-disable-next-line class-methods-use-this
  requireItemId(itemId) {
    if (itemId === null || itemId === undefined || itemId === '') {
      throw new Error('Kanban item id is required');
    }
    const raw = String(itemId).trim();
    // IDs de PK sao inteiros positivos; 0/"0" tambem sao invalidos
    if (!/^[1-9]\d*$/.test(raw)) {
      throw new Error(`Invalid kanban item id: ${itemId}`);
    }
    return raw;
  }

  itemUrl(itemId, ...parts) {
    const id = this.requireItemId(itemId);
    const base = `${this.url}/${id}`;
    return parts.length ? `${base}/${parts.join('/')}` : base;
  }

  attachmentItemUrl(itemId, ...parts) {
    const id = this.requireItemId(itemId);
    const base = `${this.baseUrl()}/kanban/items/${id}/attachments`;
    return parts.length ? `${base}/${parts.join('/')}` : base;
  }


  // SiteUp: só abre o popup quando o scheduled_at mudou de fato.
  // Updates de valor/título que reenviam item_details com o mesmo compromisso
  // NÃO devem reabrir "Confirmar agendamento".
  maybeEmitAppointmentScheduled(itemId, data) {
    if (data?.skip_appointment_emit) return;
    const det = data?.item_details || data?.kanban_item?.item_details;
    if (!det || det.scheduling_type !== 'schedule' || !det.scheduled_at) {
      return;
    }

    const current =
      store.getters['kanban/getItemById'](Number(itemId)) || {};
    const prevRaw = current?.item_details?.scheduled_at;
    if (prevRaw) {
      const prevTs = new Date(prevRaw).getTime();
      const nextTs = new Date(det.scheduled_at).getTime();
      if (
        Number.isFinite(prevTs) &&
        Number.isFinite(nextTs) &&
        prevTs === nextTs
      ) {
        return;
      }
    }

    emitter.emit('kanban:appointment:scheduled', {
      itemId,
      scheduledAt: det.scheduled_at,
    });
  }

  // SiteUp: override do update base (ApiClient) pra também emitir o evento —
  // o painel da conversa (KanbanBasicDataPanel) salva por aqui.
  async update(itemId, data) {
    const { skip_appointment_emit, ...payload } = data || {};
    const response = await axios.patch(this.itemUrl(itemId), payload);
    if (!skip_appointment_emit) {
      this.maybeEmitAppointmentScheduled(itemId, payload);
    }
    return response;
  }

  get accountId() {
    return store.state.auth.currentUser.account_id;
  }

  async getItems(
    funnelId,
    page = 1,
    stageId = null,
    agentId = null,
    conversationDisplayId = null,
    { hasConversation = false, perPage = null } = {}
  ) {
    const selectedFunnel = store.getters['funnel/getSelectedFunnel'];
    const finalFunnelId = funnelId || selectedFunnel?.id;


    if (!finalFunnelId) {
      throw new Error('Nenhum funil selecionado ou fornecido');
    }

    const params = {
      funnel_id: finalFunnelId,
      stage_id: stageId, // Sempre incluir stage_id para evitar itens ocultos
      page: page > 1 ? page : undefined,
      per_page: perPage || undefined,
      agent_id: agentId || undefined,
      conversation: conversationDisplayId || undefined,
      has_conversation: hasConversation ? true : undefined,
    };

    // Remover parâmetros undefined
    Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);


    // Usar axios diretamente para garantir que os parâmetros sejam enviados
    return axios.get(this.url, { params });
  }

  async getItemsByConversation(conversationDisplayId, page = 1) {
    if (!conversationDisplayId) return { data: { items: [], pagination: {} } };
    const params = { conversation: conversationDisplayId, page: page > 1 ? page : undefined };
    Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
    return axios.get(this.url, { params });
  }

  async getItem(itemId) {
    const response = await axios.get(this.itemUrl(itemId));
    return response;
  }

  async createItem(data) {
    try {
      const response = await axios.post(this.url, {
        kanban_item: data,
      });
      await webhookService.notifyItemCreated(response.data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateItem(itemId, data) {
    // SiteUp: snapshot vem do store (sem GET prévio — 1 request por update).
    const currentItem =
      store.getters['kanban/getItemById'](Number(itemId)) || {};
    const { skip_appointment_emit, ...payload } = data || {};
    const response = await axios.put(this.itemUrl(itemId), {
      kanban_item: payload,
    });

    const changes = {};
    Object.keys(payload).forEach(key => {
      if (JSON.stringify(currentItem[key]) !== JSON.stringify(payload[key])) {
        changes[key] = {
          from: currentItem[key],
          to: payload[key],
        };
      }
    });

    if (Object.keys(changes).length > 0) {
      await webhookService.notifyItemUpdated(response.data, changes);
    }

    // SiteUp: dispara o popup de agendamento se este update salvou um compromisso.
    if (!skip_appointment_emit) {
      this.maybeEmitAppointmentScheduled(itemId, payload);
    }

    return response;
  }

  // SiteUp: agendamento WAHA (popup Vue — substitui o script injetado).
  // Endpoints ficam sob /kanban_appointment (não /kanban_items), derivados da
  // mesma URL account-scoped do ApiClient.
  get appointmentUrl() {
    return this.url.replace(/\/kanban_items$/, '/kanban_appointment');
  }

  async previewAppointment(itemId, payload) {
    return axios.post(
      `${this.appointmentUrl}/preview?kanban_item_id=${this.requireItemId(itemId)}`,
      payload
    );
  }

  async sendAppointmentConfirmation(itemId, payload) {
    return axios.post(
      `${this.appointmentUrl}/send_confirmation?kanban_item_id=${this.requireItemId(itemId)}`,
      payload
    );
  }

  // Lê o bloco appointment_waha do kanban_config (pra saber se o toggle está ON
  // antes de abrir o popup — toggle OFF = só agenda/Google, sem WhatsApp).
  async getAppointmentConfig() {
    const url = this.url.replace(/\/kanban_items$/, '/kanban_config');
    const { data } = await axios.get(url);
    return data?.config?.appointment_waha || {};
  }


  async bulkDelete(itemIds) {
    return axios.post(`${this.url}/bulk_destroy`, { ids: itemIds });
  }

  async deleteItem(itemId) {
    // SiteUp: sem GET prévio — o snapshot pro webhook vem do store.
    const currentItem = store.getters['kanban/getItemById'](Number(itemId));
    const response = await axios.delete(this.itemUrl(itemId));

    await webhookService.notifyItemDeleted(
      currentItem || { id: Number(itemId) }
    );
    return response;
  }

  async moveToStage(itemId, funnelStage, funnelId = null) {
    // SiteUp: 1 request só — o POST move_to_stage já devolve o item completo
    // (kanban_item_full_json); a etapa de origem vem do store.
    const fromStage =
      store.getters['kanban/getItemById'](Number(itemId))?.funnel_stage ||
      null;

    // Monta o payload dinamicamente
    const payload = {
      funnel_stage: funnelStage,
      stage_entered_at: new Date().toISOString(),
    };
    if (funnelId) {
      payload.funnel_id = funnelId;
    }

    const { data: updatedItem } = await axios.post(
      this.itemUrl(itemId, 'move_to_stage'),
      payload
    );

    if (!updatedItem) return null;

    await webhookService.notifyStageChange(updatedItem, fromStage, funnelStage);

    return updatedItem;
  }

  async moveToFunnel(itemId, funnelId, funnelStage = null) {
    try {
      const { data: currentItem } = await this.getItem(itemId);
      const oldFunnelId = currentItem.funnel_id;
      const oldStage = currentItem.funnel_stage;

      const payload = {
        funnel_id: funnelId,
      };
      if (funnelStage) {
        payload.funnel_stage = funnelStage;
        payload.stage_entered_at = new Date().toISOString();
      }

      const response = await axios.post(this.itemUrl(itemId, 'move'), payload);

      const { data: updatedItem } = await this.getItem(itemId);

      if (updatedItem) {
        // Notificar mudança de funil ou estágio
        const changes = {};
        if (oldFunnelId !== funnelId) {
          changes.funnel_id = { from: oldFunnelId, to: funnelId };
        }
        if (funnelStage && oldStage !== funnelStage) {
          changes.funnel_stage = { from: oldStage, to: funnelStage };
          await webhookService.notifyStageChange(
            updatedItem,
            oldStage,
            funnelStage
          );
        }

        if (Object.keys(changes).length > 0) {
          await webhookService.notifyItemUpdated(updatedItem, changes);
        }

        return updatedItem;
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  }

  async reorderItems(positions) {
    try {
      const currentItems = await Promise.all(
        positions.map(pos => this.getItem(pos.id))
      );

      const response = await axios.post(`${this.url}/reorder`, {
        positions,
      });

      const changes = positions.map(pos => ({
        item_id: pos.id,
        old_position: currentItems.find(item => item.data.id === pos.id)?.data
          .position,
        new_position: pos.position,
      }));

      await webhookService.notifyItemReordered(response.data, changes);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async startTimer(itemId) {
    try {
      const response = await this.updateItem(itemId, {
        timer_started_at: new Date().toISOString(),
      });

      await webhookService.notifyTimerStarted(response.data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async stopTimer(itemId) {
    try {
      const response = await this.updateItem(itemId, {
        timer_started_at: null,
      });

      const duration = response.data.timer_duration;
      await webhookService.notifyTimerStopped(response.data, duration);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getItemByConversationId(conversationId) {
    return this.getItemsByConversation(conversationId);
  }

  // Métodos para gerenciar agentes atribuídos
  async assignAgent(itemId, agentId) {
    try {
      const response = await axios.post(this.itemUrl(itemId, 'assign_agent'), {
        agent_id: Number(agentId),
      });

      // SiteUp: upsert local com o response (updateItemDetails disparava um
      // 2º PUT com o item inteiro). Não deixar falha do store mascarar sucesso.
      try {
        store.dispatch('kanban/applyRealtimeUpsert', response.data);
      } catch (e) {
        // no-op
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async removeAgent(itemId, agentId) {
    try {
      const response = await axios.delete(this.itemUrl(itemId, 'remove_agent'), {
        data: {
          agent_id: agentId,
        },
      });

      // SiteUp: upsert local com o response (updateItemDetails disparava um
      // 2º PUT com o item inteiro)
      store.dispatch('kanban/applyRealtimeUpsert', response.data);

      return response;
    } catch (error) {
      throw error;
    }
  }

  async getAssignedAgents(itemId) {
    try {
      const response = await axios.get(this.itemUrl(itemId, 'assigned_agents'));
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBatchData(funnelId) {
    const response = await axios.get(`${this.url}/batch`, {
      params: {
        funnel_id: funnelId,
      },
    });

    return response.data;
  }

  // Busca itens em TODOS os funis da conta (sem filtrar por funil).
  // Usado pelo picker "Duplicar de outro kanban" no form de criação.
  async searchItemsGlobal(query) {
    const q = (query || '').trim();
    if (!q) return { data: { items: [], total: 0 } };
    const response = await axios.get(`${this.url}/search`, {
      params: { query: q },
    });
    return response;
  }

  async searchItems(query, funnelId = null, agentId = null) {
    try {
      const selectedFunnel = store.getters['funnel/getSelectedFunnel'];
      const finalFunnelId = funnelId || selectedFunnel?.id;

      if (!finalFunnelId) {
        throw new Error('Nenhum funil selecionado ou fornecido');
      }

      const params = {
        query: query,
        funnel_id: finalFunnelId,
        agent_id: agentId || undefined,
      };

      // Remover parâmetros undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await axios.get(`${this.url}/search`, { params });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async filterItems(filters = {}, funnelId = null) {
    try {
      const selectedFunnel = store.getters['funnel/getSelectedFunnel'];
      const finalFunnelId = funnelId || selectedFunnel?.id;

      if (!finalFunnelId) {
        throw new Error('Nenhum funil selecionado ou fornecido');
      }

      const params = {
        funnel_id: finalFunnelId,
        priorities: filters.priorities || undefined,
        value_min: filters.valueMin || undefined,
        value_max: filters.valueMax || undefined,
        agent_id: filters.agent || undefined,
        conversation: filters.conversationDisplayId || undefined,
        date_start: filters.dateStart || undefined,
        date_end: filters.dateEnd || undefined,
        scheduled_date_start: filters.scheduledDateStart || undefined,
        scheduled_date_end: filters.scheduledDateEnd || undefined,
      };

      // Remover parâmetros undefined ou arrays vazios
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || (Array.isArray(params[key]) && params[key].length === 0)) {
          delete params[key];
        }
      });

      const response = await axios.get(`${this.url}/filter`, { params });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // SiteUp: Linha do tempo de compras do lead — agrega as ofertas de todos os
  // kanban_items vinculados a qualquer conversa do contato.
  // GET kanban_items/offers_timeline?contact_id=
  async getOffersTimelineByContact(contactId) {
    return axios.get(`${this.url}/offers_timeline`, {
      params: { contact_id: contactId },
    });
  }

  async getReports(params = {}) {
    try {
      const requestParams = {
        funnel_id: params.funnel_id,
        from: params.from,
        to: params.to,
        user_ids: params.user_ids,
        inbox_id: params.inbox_id,
        channel: params.channel,
      };
      Object.keys(requestParams).forEach(key => {
        const v = requestParams[key];
        if (v === undefined || v === null || v === '') {
          delete requestParams[key];
        }
      });
      const response = await axios.get(`${this.url}/reports`, {
        params: requestParams,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atribuir agente a um item específico do checklist
  async assignAgentToChecklistItem(itemId, checklistItemId, agentId) {
    try {
      const response = await axios.post(this.itemUrl(itemId, 'assign_agent_to_checklist_item'), {
        checklist_item_id: checklistItemId,
        agent_id: agentId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para remover agente de um item específico do checklist
  async removeAgentFromChecklistItem(itemId, checklistItemId) {
    try {
      const response = await axios.delete(this.itemUrl(itemId, 'remove_agent_from_checklist_item'), {
        data: {
          checklist_item_id: checklistItemId,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para criar checklist item
  async createChecklistItem(itemId, checklistData) {
    try {
      const response = await axios.post(this.itemUrl(itemId, 'create_checklist_item'), {
        text: checklistData.text,
        attachments: checklistData.attachments || [],
        linked_item_id: checklistData.linked_item_id,
        linked_conversation_id: checklistData.linked_conversation_id,
        linked_contact_id: checklistData.linked_contact_id,
        due_date: checklistData.due_date,
        priority: checklistData.priority || 'none',
        agent_id: checklistData.agent_id,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atualizar checklist item
  async updateChecklistItem(itemId, checklistItemId, updateData) {
    try {
      const response = await axios.patch(this.itemUrl(itemId, 'update_checklist_item'), {
        checklist_item_id: checklistItemId,
        ...updateData
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para marcar/desmarcar item do checklist
  async toggleChecklistItem(itemId, checklistItemId) {
    try {
      const response = await axios.post(this.itemUrl(itemId, 'toggle_checklist_item'), {
        checklist_item_id: checklistItemId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para deletar checklist item
  async deleteChecklistItem(itemId, checklistItemId) {
    try {
      const response = await axios.delete(this.itemUrl(itemId, 'delete_checklist_item'), {
        data: {
          checklist_item_id: checklistItemId
        }
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para criar nota
  async createNote(itemId, noteData) {
    try {
      const response = await axios.post(this.itemUrl(itemId, 'create_note'), {
        text: noteData.text,
        attachments: noteData.attachments || [],
        linked_item_id: noteData.linked_item_id,
        linked_conversation_id: noteData.linked_conversation_id,
        linked_contact_id: noteData.linked_contact_id,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atualizar nota
  async updateNote(itemId, noteId, noteData) {
    try {
      const response = await axios.patch(this.itemUrl(itemId, 'update_note'), {
        note_id: noteId,
        text: noteData.text,
        attachments: noteData.attachments || [],
        linked_item_id: noteData.linked_item_id,
        linked_conversation_id: noteData.linked_conversation_id,
        linked_contact_id: noteData.linked_contact_id,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para buscar notas do item
  async getNotes(itemId) {
    try {
      const response = await axios.get(this.itemUrl(itemId, 'get_notes'));
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para buscar checklist do item
  async getChecklists(itemId) {
    try {
      const response = await axios.get(this.itemUrl(itemId, 'get_checklist'));
      return response;
    } catch (error) {
      throw error;
    }
  }


  // Método para deletar nota
  async deleteNote(itemId, noteId) {
    try {
      const response = await axios.delete(this.itemUrl(itemId, 'delete_note'), {
        data: {
          note_id: noteId
        }
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para fazer upload de anexo do item kanban
  async uploadAttachment(itemId, file) {
    try {
      const formData = new FormData();
      formData.append('attachment', file);

      // Usar a URL correta para attachments: kanban/items ao invés de kanban_items
      const attachmentUrl = this.attachmentItemUrl(itemId);

      const response = await axios.post(attachmentUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para deletar anexo do item kanban
  async deleteAttachment(itemId, attachmentId) {
    try {
      // Usar a URL correta para attachments: kanban/items ao invés de kanban_items
      const attachmentUrl = this.attachmentItemUrl(itemId, attachmentId);

      const response = await axios.delete(attachmentUrl);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter todos os anexos de um item kanban
  async getAttachments(itemId) {
    try {
      // Usar a URL correta para attachments: kanban/items ao invés de kanban_items
      const attachmentUrl = this.attachmentItemUrl(itemId);

      const response = await axios.get(attachmentUrl);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter relatório de tempo do item
  async getTimeReport(itemId) {
    try {
      const response = await axios.get(this.itemUrl(itemId, 'time_report'));
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter detalhamento de tempo por etapa
  async getStageTimeBreakdown(itemId) {
    try {
      const response = await axios.get(this.itemUrl(itemId, 'stage_time_breakdown'));
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para duplicar checklist para outro item
  async duplicateChecklist(sourceItemId, targetItemId, options = {}) {
    try {
      const response = await axios.post(this.itemUrl(sourceItemId, 'duplicate_checklist'), {
        target_item_id: targetItemId,
        merge: options.merge || false
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para buscar itens no checklist
  async searchChecklist(itemId, query = '') {
    try {
      const params = query ? { query } : {};
      const response = await axios.get(this.itemUrl(itemId, 'search_checklist'), { params });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter progresso do checklist por agente
  async getChecklistProgressByAgent(itemId) {
    try {
      const response = await axios.get(this.itemUrl(itemId, 'checklist_progress_by_agent'));
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter counts de notas, checklist e anexos
  async getCounts(itemId) {
    try {
      const response = await axios.get(this.itemUrl(itemId, 'counts'));
      return response;
    } catch (error) {
      throw error;
    }
  }

  // AÇÕES EM MASSA

  // Método para mover múltiplos itens para uma nova etapa
  async bulkMoveItems(itemIds, newStage, funnelId = null) {
    try {
      const payload = {
        item_ids: itemIds,
        new_stage: newStage
      };

      if (funnelId) {
        payload.funnel_id = funnelId;
      }

      const response = await axios.post(`${this.url}/bulk_move_items`, payload);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atribuir agente para múltiplos itens
  async bulkAssignAgent(itemIds, agentId, mode = 'replace') {
    try {
      const response = await axios.post(`${this.url}/bulk_assign_agent`, {
        item_ids: itemIds,
        agent_id: agentId,
        mode: mode // 'replace' ou 'add'
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para definir prioridade para múltiplos itens
  async bulkSetPriority(itemIds, priority) {
    try {
      const response = await axios.post(`${this.url}/bulk_set_priority`, {
        item_ids: itemIds,
        priority: priority
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para mudar o status do item
  async changeStatus(itemId, status) {
    try {
      const response = await axios.post(this.itemUrl(itemId, 'change_status'), {
        status: status
      });

      await webhookService.notifyItemUpdated(response.data, {
        status: { from: null, to: status }
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para exportar itens do funil em CSV
  async exportItems(funnelId, filters = {}) {
    try {
      const params = {
        funnel_id: funnelId,
        priorities: filters.priorities || undefined,
        value_min: filters.valueMin || undefined,
        value_max: filters.valueMax || undefined,
        agent_id: filters.agent || undefined,
        date_start: filters.dateStart || undefined,
        date_end: filters.dateEnd || undefined,
        scheduled_date_start: filters.scheduledDateStart || undefined,
        scheduled_date_end: filters.scheduledDateEnd || undefined,
        // SiteUp: filtros usados pelo relatório do Kanban (período/agentes/canal).
        // from/to são epoch (ms ou s) e filtram por created_at no /export.
        // user_ids permite múltiplos agentes; channel filtra pelo canal da inbox.
        from: filters.from || undefined,
        to: filters.to || undefined,
        user_ids:
          Array.isArray(filters.userIds) && filters.userIds.length > 0
            ? filters.userIds
            : undefined,
        channel: filters.channel || undefined,
      };

      // Remover parâmetros undefined ou arrays vazios
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || (Array.isArray(params[key]) && params[key].length === 0)) {
          delete params[key];
        }
      });

      const response = await axios.get(`${this.url}/export`, {
        params,
        responseType: 'blob'
      });

      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extrair nome do arquivo do header Content-Disposition ou usar padrão
      const contentDisposition = response.headers['content-disposition'];
      let filename = `kanban_items_${funnelId}_${new Date().getTime()}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para fazer preview do CSV antes da importação
  async importPreview(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${this.url}/import_preview`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para importar itens do CSV
  async importItems(funnelId, file, mappings, defaultStageId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('funnel_id', funnelId);
      formData.append('mappings', JSON.stringify(mappings));
      if (defaultStageId) {
        formData.append('default_stage_id', defaultStageId);
      }

      const response = await axios.post(`${this.url}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  async aiUpdate(itemId) {
    return axios.post(this.itemUrl(itemId, 'ai_update'));
  }

  // funnelId é opcional: quando a conta tem a regra global ligada, o backend
  // escolhe o funil e ignora esse campo.
  async aiAutoCreate({ conversationId, funnelId }) {
    return axios.post(`${this.url}/ai_auto_create`, {
      conversation_id: String(conversationId),
      funnel_id: funnelId,
    });
  }

  // SiteUp: regra GLOBAL de auto-criação por IA (multi-funil), na conta.
  // Reusa o baseUrl account-scoped do ApiClient em vez de remontar a URL na mão.
  get aiSettingsUrl() {
    return `${this.baseUrl()}/kanban_ai_settings`;
  }

  async getGlobalAiSettings() {
    return axios.get(this.aiSettingsUrl);
  }

  async updateGlobalAiSettings(config) {
    return axios.put(this.aiSettingsUrl, {
      kanban_ai_auto_create: config,
    });
  }
}

export default new KanbanAPI();
